// Glossary of CFO-Ready Financial Terms for OncAccess
// Each term includes a short definition and a detailed explanation

export const GLOSSARY = {
  // Primary Liability Terms
  unfundedExposure: {
    term: "Unfunded Exposure",
    short: "Hidden liability risk if treatment fails",
    full: "The estimated amount the patient or family will have to pay out-of-pocket for emergency complications if this therapy fails or becomes unaffordable. Calculated as (Probability of Discontinuation) × (Cost of Rescue Therapy)."
  },
  
  totalLiability: {
    term: "Total Liability",
    short: "Complete downstream risk exposure",
    full: "The sum of all potential costs associated with treatment failure, including hospitalization, lost productivity, and toxicity management. Formula: (P_crash × C_ICU) + (T_leak × C_prod) + C_toxicity"
  },
  
  // Probability & Risk Terms
  pCrash: {
    term: "P(Crash)",
    short: "Discontinuation probability",
    full: "Probability of early treatment discontinuation within 6 months. Calculated as max(0, 1 - (Median PFS / 6)). A higher value indicates greater risk of treatment failure."
  },
  
  riskWeightedCostIndex: {
    term: "Risk-Weighted Cost Index",
    short: "Liability-adjusted cost ratio",
    full: "The ratio of total liability to base drug cost. Values >1.0x indicate significant hidden costs. This metric helps CFOs understand the true financial exposure beyond the sticker price."
  },
  
  // Cost Components
  cIcu: {
    term: "C_ICU",
    short: "Emergency hospitalization cost",
    full: "Expected ICU or hospitalization cost from treatment discontinuation. Calculated as regional ICU cost × probability of crash. This represents the 'rescue cost' when treatment fails."
  },
  
  cToxicity: {
    term: "C_Toxicity",
    short: "Adverse event management cost",
    full: "Expected cost of managing Grade 3/4 adverse events compared to the competitor. Formula: (Competitor AE Rate × 3 × Regional AE Management Cost). Higher competitor AE rates increase hidden costs."
  },
  
  cProductivity: {
    term: "C_Productivity",
    short: "Lost productivity cost",
    full: "Economic cost of lost work days during treatment gap or recovery period. Calculated as Regional Daily Wage × Treatment Leak Period (typically 6 months = 180 days)."
  },
  
  toxicityTax: {
    term: "Toxicity Tax",
    short: "Hidden AE management burden",
    full: "Additional cost imposed by adverse event management. This 'tax' is often invisible in headline pricing but significantly impacts total cost of care."
  },
  
  // AE Terms
  aeRate: {
    term: "AE Rate (Gr 3/4)",
    short: "Severe adverse event frequency",
    full: "Percentage of patients experiencing Grade 3 or 4 adverse events. Grade 3 = severe, Grade 4 = life-threatening. Higher rates indicate greater toxicity management costs."
  },
  
  aeMgmtCost: {
    term: "AE Mgmt. Cost",
    short: "Cost of managing side effects",
    full: "Estimated cost of managing adverse events during treatment. Includes hospitalization, supportive care, and dose modifications. No drug has zero AE cost - minimum 5% rate assumed."
  },
  
  // Clinical Endpoints
  medianPfs: {
    term: "Median PFS",
    short: "Time without disease progression",
    full: "Median Progression-Free Survival - the time at which 50% of patients have disease progression. Longer PFS indicates better treatment efficacy and lower crash probability."
  },
  
  medianOs: {
    term: "Median OS",
    short: "Overall survival time",
    full: "Median Overall Survival - the time at which 50% of patients are still alive. The gold standard endpoint for oncology, though PFS is often used as a surrogate."
  },
  
  hazardRatio: {
    term: "Hazard Ratio (HR)",
    short: "Relative risk vs comparator",
    full: "The ratio of hazard (risk of event) between treatment and control arms. HR < 1.0 means the treatment reduces risk. HR = 0.50 means 50% risk reduction."
  },
  
  orr: {
    term: "ORR",
    short: "Objective Response Rate",
    full: "Percentage of patients whose tumor shrinks or disappears. Includes complete response (CR) and partial response (PR). Higher ORR indicates better initial efficacy."
  },
  
  dor: {
    term: "DOR",
    short: "Duration of Response",
    full: "How long the treatment response lasts before progression. Longer DOR indicates more durable benefit and better value for the investment."
  },
  
  dcr: {
    term: "DCR",
    short: "Disease Control Rate",
    full: "Percentage of patients with tumor shrinkage or stable disease. Includes complete response, partial response, and stable disease. Broader measure than ORR."
  },
  
  notReached: {
    term: "Not Reached (NR)",
    short: "Endpoint not yet achieved",
    full: "Median survival endpoint has not been reached in the study population. This is positive - it means more than half the patients are still alive or progression-free. Estimated using Hazard Ratio when available."
  },
  
  // Pricing Terms
  effectivePatientCost: {
    term: "Effective Patient Cost",
    short: "Actual out-of-pocket cost",
    full: "The real cost to the patient after PAP schemes, co-pay assistance, and insurance adjustments. May be significantly lower than list price."
  },
  
  listPrice: {
    term: "List Price",
    short: "Published headline price",
    full: "The official price per cycle before any discounts, schemes, or assistance programs. This is the 'sticker price' but rarely what patients actually pay."
  },
  
  papScheme: {
    term: "PAP Scheme",
    short: "Patient Assistance Program",
    full: "Free or discounted drug programs offered by manufacturers. Common schemes include Buy 1 Get 1 Free, Cap programs, and income-based assistance."
  },
  
  // Competitor Terms
  competitorBaseCost: {
    term: "Competitor Base Cost",
    short: "Standard of Care price",
    full: "The baseline cost of the current standard treatment. Used as a benchmark to demonstrate the value proposition of the new therapy."
  },
  
  // Discontinuation Terms
  tLeak: {
    term: "T_Leak",
    short: "Treatment gap period",
    full: "Time between treatment discontinuation and next therapy initiation. Default 6 months. During this period, patients incur productivity losses and may have emergency events."
  },
  
  discontinuationCliff: {
    term: "Discontinuation Cliff",
    short: "Financial risk of stopping early",
    full: "The sudden spike in costs when treatment is discontinued prematurely. Demonstrates that 'saving money' by stopping treatment actually increases total cost due to rescue therapy needs."
  },
  
  rescueCost: {
    term: "Rescue Cost",
    short: "Emergency intervention expense",
    full: "The cost of managing a patient who crashes after discontinuation. Includes ICU, emergency surgery, and palliative care. Formula: 40% × Total Liability."
  },
  
  // Source & Data Terms
  localHero: {
    term: "Local Hero",
    short: "Regional data priority",
    full: "Regional Centers of Excellence (e.g., TMC, AIIMS in India) whose data is prioritized over global sources. Ensures locally relevant clinical evidence."
  },
  
  dataTier: {
    term: "Data Tier",
    short: "Source reliability level",
    full: "Trust hierarchy for data sources. Tier 1: FDA/NEJM/Lancet (highest), Tier 2: NCCN/cancer.gov, Tier 3: News sources. Lower tier = higher reliability."
  },
  
  confidence: {
    term: "Confidence",
    short: "Data reliability score",
    full: "Percentage indicating how reliable the extracted data is. High (80%+): from primary sources. Medium (40-80%): secondary sources. Low (<40%): estimated or derived."
  }
};

// Helper function to get tooltip content
export const getTooltip = (key) => {
  const entry = GLOSSARY[key];
  if (!entry) return null;
  return {
    term: entry.term,
    content: entry.full
  };
};

// Helper to get short definition
export const getShortDef = (key) => {
  const entry = GLOSSARY[key];
  return entry?.short || '';
};
